import { watchImmediate } from "@vueuse/core";
import { nextTick, ref } from "vue";
import { getSlotContentByTagName } from "#ce/utils/slots";

/**
 * Equalize heights of slotted children by measuring tallest item.
 *
 * @param {import("vue").Ref<HTMLElement|null>} groupRoot
 * @param {string} tagName
 * @param {import("vue").ComputedRef<boolean>} isEqualize
 * @param {Array<import("vue").Ref | import("vue").ComputedRef>} deps
 * @returns {{ equalizedHeight: import("vue").Ref<string> }}
 */
export function useEqualizeHeights(groupRoot, tagName, isEqualize, deps = []) {
  const elements = ref([]);
  const equalizedHeight = ref("0px");

  function updateElementsToEqualize() {
    elements.value = getSlotContentByTagName(groupRoot.value, tagName);
  }

  async function measure() {
    equalizedHeight.value = "0px";
    await nextTick();

    const heights = elements.value.map((el) => el.getBoundingClientRect?.().height || el.offsetHeight || 0);
    const max = Math.max(...heights, 0);
    equalizedHeight.value = `${Math.round(max)}px`;
  }

  async function refresh() {
    await nextTick();
    if (isEqualize.value) {
      await measure();
    } else {
      equalizedHeight.value = "0px";
    }
  }

  watchImmediate(groupRoot, updateElementsToEqualize);

  watchImmediate([elements, isEqualize, ...deps], refresh, { flush: "post" });

  return { equalizedHeight };
}
